import React, { useState, useEffect } from 'react';
import ChevronDown from '../../assets/icons/common/chevron-down.svg';
import ChevronUp from '../../assets/icons/common/chevron-up-active.svg';

// Nested menu item component (for items like Raptor, Singlewire, Centegix with their own submenus)
function NestedMenuItem({ subItem, selected, handleClickEvent }) {
  const [nestedIsOpen, setNestedIsOpen] = useState(() => {
    return localStorage.getItem(`${subItem.id}IsOpen`) === 'true';
  });

  useEffect(() => {
    if (subItem.submenu) {
      localStorage.setItem(`${subItem.id}IsOpen`, nestedIsOpen);
    }
  }, [nestedIsOpen, subItem.id, subItem.submenu]);

  const toggleNestedSubmenu = (e) => {
    e.stopPropagation();
    setNestedIsOpen(!nestedIsOpen);
  };

  const isNestedActive = subItem.submenu && Object.values(subItem.submenu).some(
    nestedItem => nestedItem.path === selected
  );

  const NestedIconComponent = subItem.icon;

  return (
    <li key={subItem.id} className="nav-item subitem nested-integration">
      <div
        id={subItem.id}
        className={`nav-link sublink ${isNestedActive ? 'active' : ''}`}
        onClick={toggleNestedSubmenu}
      >
        {NestedIconComponent && <NestedIconComponent className="sidebar-icon" />}
        <span className="menu-text align-text-bottom">
          {subItem.label}
        </span>
        <img
          src={nestedIsOpen ? ChevronUp : ChevronDown}
          alt={nestedIsOpen ? 'Collapse' : 'Expand'}
          className="chevron-icon"
        />
      </div>
      {subItem.submenu && nestedIsOpen && (
        <ul className="nav flex-column nested-submenu active">
          {Object.values(subItem.submenu).map((nestedItem) => (
            <li key={nestedItem.id} className="nav-item subitem nested-subitem">
              <div
                id={nestedItem.id}
                className={`nav-link sublink ${selected === nestedItem.path ? 'active' : ''}`}
                onClick={(e) => handleClickEvent(e, nestedItem.path)}
              >
                <span className="menu-text align-text-bottom">
                  {nestedItem.label}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function MenuItem({ item, selected, handleClickEvent, isSidebarOpen }) {
  const { id, label, path, icon: IconComponent, submenu } = item;
  const hasSubmenu = !!submenu;
  
  const [isOpen, setIsOpen] = useState(() => {
    return localStorage.getItem(`${id}IsOpen`) === 'true';
  });

  useEffect(() => {
    if (hasSubmenu) {
      localStorage.setItem(`${id}IsOpen`, isOpen);
    }
  }, [isOpen, id, hasSubmenu]);

  // Check if any submenu item is selected (including nested items with their own submenus)
  const isSubmenuItemSelected = () => {
    if (!submenu) return false;
    return Object.values(submenu).some(subItem => {
      if (subItem.path === selected) return true;
      // Check nested submenu items
      if (subItem.submenu) {
        return Object.values(subItem.submenu).some(nestedItem => nestedItem.path === selected);
      }
      return false;
    });
  };

  const toggleSubmenu = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const isCollapsed = !isSidebarOpen;
  const isActive = hasSubmenu 
    ? isSubmenuItemSelected() 
    : selected === path;

  const renderSubmenu = () => {
    if (!submenu || isCollapsed) return null;

    return (
      <ul className={`nav flex-column submenu ${isOpen ? 'active' : ''}`}>
        {Object.values(submenu).map((subItem) => {
          // Render separator (styled like regular submenu item but not clickable)
          if (subItem.isSeparator) {
            return (
              <li key={subItem.id} className="nav-item subitem">
                <div className="nav-link sublink separator-item">
                  <span className="menu-text align-text-bottom text-nowrap">
                    {subItem.label}
                  </span>
                </div>
              </li>
            );
          }
          
          // Render nested item with its own submenu
          if (subItem.isNested && subItem.submenu) {
            return (
              <NestedMenuItem 
                key={subItem.id}
                subItem={subItem}
                selected={selected}
                handleClickEvent={handleClickEvent}
              />
            );
          }
          
          // Render regular submenu item
          return (
            <li key={subItem.id} className="nav-item subitem">
              <div
                id={subItem.id}
                className={`nav-link sublink ${selected === subItem.path ? 'active' : ''}`}
                onClick={(e) => handleClickEvent(e, subItem.path)}
              >
                <span className="menu-text align-text-bottom text-nowrap">
                  {subItem.label}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const navLink = (
    <div
      id={id}
      className={`nav-link ${isCollapsed ? 'collapsed' : ''} ${hasSubmenu ? 'has-submenu' : ''} ${isActive ? 'active' : ''}`}
      onClick={hasSubmenu ? toggleSubmenu : (e) => handleClickEvent(e, path)}
    >
      <IconComponent className="sidebar-icon" />
      {!isCollapsed && (
        <>
          <span className="menu-text align-text-bottom text-nowrap">{label}</span>
          {hasSubmenu && (
            <img
              src={isOpen ? ChevronUp : ChevronDown}
              alt={isOpen ? 'Collapse' : 'Expand'}
              className="chevron-icon"
            />
          )}
        </>
      )}
    </div>
  );

  return (
    <li className="nav-item">
      <div className={`menu-container ${hasSubmenu && isOpen ? 'submenu-active' : ''}`}>
        {navLink}
        {renderSubmenu()}
      </div>
    </li>
  );
}

export default MenuItem;